import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  TrendingUp, RefreshCw, BookOpen, Lightbulb, Search,
  PenSquare, ChevronRight, X, Zap, Globe,
} from 'lucide-react'
import { Card, Badge, PageHeader, EmptyState } from '../components/Card'
import { listTrends, fetchTrends, trendsDigest, trendsplan, createDraft, type TrendSignal } from '../api/client'
import { layout } from '../utils/layout'

// Derive actionable content opportunities from top trend signals
function buildOpportunities(signals: TrendSignal[]) {
  const hot = [...signals].sort((a, b) => b.velocity - a.velocity).slice(0, 5)
  return hot.map(s => ({
    signal: s,
    angle: getAngle(s.topic, s.source),
    format: getFormat(s.velocity),
    cta: `Create a post about "${s.topic}" while it's trending on ${s.source}`,
  }))
}

function getAngle(topic: string, source: string): string {
  const angles = [
    `How ${topic} is changing the industry`,
    `5 things you need to know about ${topic}`,
    `Why ${topic} matters for your audience`,
    `The truth about ${topic} nobody talks about`,
    `Our take on the ${topic} conversation`,
  ]
  return angles[Math.abs(topic.length + source.length) % angles.length]
}

function getFormat(velocity: number): string {
  if (velocity > 0.7) return 'carousel'
  if (velocity > 0.4) return 'short video'
  return 'text post'
}

export default function TrendsView() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [deepDive, setDeepDive] = useState<TrendSignal | null>(null)
  const [creatingFrom, setCreatingFrom] = useState<string | null>(null)
  const [draftContent, setDraftContent] = useState('')

  const { data: trends, isLoading } = useQuery({
    queryKey: ['trends'],
    queryFn: () => listTrends(30),
  })

  const { data: digest } = useQuery({
    queryKey: ['trends-digest'],
    queryFn: () => trendsDigest(),
  })

  const fetchMut = useMutation({
    mutationFn: fetchTrends,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trends'] }),
  })

  const planMut = useMutation({
    mutationFn: () => trendsplan(5),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }),
  })

  const createDraftMut = useMutation({
    mutationFn: ({ content, platforms }: { content: string; platforms: string[] }) =>
      createDraft({ content, platforms }),
    onSuccess: () => {
      setCreatingFrom(null)
      setDraftContent('')
      qc.invalidateQueries({ queryKey: ['drafts'] })
    },
  })

  const filtered = (trends?.signals ?? []).filter(s =>
    !search || s.topic.toLowerCase().includes(search.toLowerCase()) || s.source.toLowerCase().includes(search.toLowerCase())
  )

  const opportunities = buildOpportunities(trends?.signals ?? [])

  const velColor = (v: number) => {
    if (v > 0.7) return 'red'
    if (v > 0.4) return 'yellow'
    return 'green'
  }

  return (
    <div>
      <PageHeader
        title="Trend Intelligence Center"
        subtitle="Real-time signals · Content opportunities · Competitor activity"
        action={
          <button
            onClick={() => fetchMut.mutate()}
            disabled={fetchMut.isPending}
            className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${fetchMut.isPending ? 'animate-spin' : ''}`} />
            {fetchMut.isPending ? 'Fetching…' : 'Fetch Trends'}
          </button>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        {/* Actions row */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => planMut.mutate()}
            disabled={planMut.isPending}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-sm px-4 py-2 rounded-lg transition-colors"
          >
            <Lightbulb className="w-4 h-4 text-yellow-400" />
            {planMut.isPending ? 'Planning…' : 'Generate Content Plan (5 posts)'}
          </button>
          {planMut.isSuccess && (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <ChevronRight className="w-3.5 h-3.5" /> 5 drafts created — check Content Studio
            </span>
          )}
        </div>

        {/* AI Digest */}
        {digest?.digest && (
          <Card title="AI Trend Digest">
            <div className="flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-brand-400 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300 leading-relaxed">{digest.digest}</p>
            </div>
          </Card>
        )}

        {/* Content Opportunities */}
        {opportunities.length > 0 && (
          <Card title="Content Opportunities">
            <div className="space-y-3">
              {opportunities.map(op => (
                <div key={op.signal.id} className="flex items-start gap-3 p-3 bg-slate-800/40 rounded-lg">
                  <Zap className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-slate-200">{op.angle}</span>
                      <Badge color="blue">{op.format}</Badge>
                      <Badge color={velColor(op.signal.velocity) as 'red' | 'yellow' | 'green'}>
                        {op.signal.velocity > 0.7 ? 'Hot' : op.signal.velocity > 0.4 ? 'Rising' : 'New'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500">{op.cta}</p>
                  </div>
                  <button
                    onClick={() => { setCreatingFrom(op.signal.id); setDraftContent(op.angle) }}
                    className="shrink-0 flex items-center gap-1 text-xs bg-brand-700 hover:bg-brand-600 text-brand-100 px-2.5 py-1.5 rounded-lg transition-colors"
                  >
                    <PenSquare className="w-3 h-3" /> Draft
                  </button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Competitor Activity (placeholder — requires platform API access) */}
        <Card title="Competitor Activity">
          <div className="flex items-start gap-3 p-3 bg-slate-800/20 rounded-lg">
            <Globe className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-slate-400">Competitor monitoring requires connected platform accounts with analytics permissions.</p>
              <p className="text-xs text-slate-500 mt-1">Connect your accounts in <span className="text-brand-400">Settings → Connections</span> to see competitor posts and engagement data here.</p>
            </div>
          </div>
        </Card>

        {/* Live signals with search */}
        <Card title={`${trends?.total ?? 0} Trend Signals`} action={
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter signals…"
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-800 border border-surface-border rounded-lg w-44 focus:outline-none focus:border-brand-500"
            />
          </div>
        }>
          {isLoading ? (
            <div className="py-8 text-center text-slate-500 text-sm">Loading signals…</div>
          ) : !filtered.length ? (
            <EmptyState
              icon={<TrendingUp className="w-12 h-12" />}
              title={search ? 'No matching signals' : 'No signals yet'}
              description={search ? `No signals match "${search}"` : 'Click Fetch Trends to pull the latest signals.'}
              action={
                !search ? (
                  <button onClick={() => fetchMut.mutate()} className="bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg">
                    Fetch Now
                  </button>
                ) : undefined
              }
            />
          ) : (
            <div className="space-y-2">
              {filtered.map(s => (
                <div
                  key={s.id}
                  className="flex items-start gap-3 p-3 bg-slate-800/40 rounded-lg hover:bg-slate-800/60 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-slate-200">{s.topic}</span>
                      <Badge color={velColor(s.velocity) as 'red' | 'yellow' | 'green'}>
                        {s.velocity > 0.7 ? 'Hot' : s.velocity > 0.4 ? 'Rising' : 'New'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="capitalize">{s.source}</span>
                      <span>Relevance: {(s.relevance_score * 100).toFixed(0)}%</span>
                      <span>Velocity: {(s.velocity * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="text-xs text-slate-500">{new Date(s.detected_at).toLocaleTimeString()}</div>
                    <button
                      onClick={() => setDeepDive(s)}
                      className="text-xs text-brand-400 hover:text-brand-300 flex items-center gap-0.5"
                    >
                      Deep Dive <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Deep Dive Modal */}
      {deepDive && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setDeepDive(null)}>
          <div className="bg-surface-card border border-surface-border rounded-2xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Deep Dive: {deepDive.topic}</h2>
              <button onClick={() => setDeepDive(null)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-slate-800 rounded-xl p-3">
                  <div className="text-xs text-slate-500 mb-1">Relevance</div>
                  <div className="text-xl font-bold text-brand-400">{(deepDive.relevance_score * 100).toFixed(0)}%</div>
                </div>
                <div className="bg-slate-800 rounded-xl p-3">
                  <div className="text-xs text-slate-500 mb-1">Velocity</div>
                  <div className="text-xl font-bold text-yellow-400">{(deepDive.velocity * 100).toFixed(0)}%</div>
                </div>
                <div className="bg-slate-800 rounded-xl p-3">
                  <div className="text-xs text-slate-500 mb-1">Source</div>
                  <div className="text-sm font-semibold text-slate-300 capitalize">{deepDive.source}</div>
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-2">Suggested content angles:</p>
                <div className="space-y-1.5">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-slate-800/60 rounded-lg text-sm text-slate-300">
                      <Lightbulb className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
                      {getAngle(deepDive.topic + i, deepDive.source)}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setDeepDive(null)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-sm py-2 rounded-lg">Close</button>
                <button
                  onClick={() => { setCreatingFrom(deepDive.id); setDraftContent(getAngle(deepDive.topic, deepDive.source)); setDeepDive(null) }}
                  className="flex-1 flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-sm py-2 rounded-lg"
                >
                  <PenSquare className="w-4 h-4" /> Create Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Draft from Opportunity Modal */}
      {creatingFrom && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setCreatingFrom(null)}>
          <div className="bg-surface-card border border-surface-border rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Quick Draft</h2>
              <button onClick={() => setCreatingFrom(null)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Draft content</label>
                <textarea
                  rows={4}
                  value={draftContent}
                  onChange={e => setDraftContent(e.target.value)}
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm resize-none"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setCreatingFrom(null)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-sm py-2 rounded-lg">Cancel</button>
                <button
                  onClick={() => createDraftMut.mutate({ content: draftContent, platforms: ['instagram', 'twitter'] })}
                  disabled={createDraftMut.isPending || !draftContent.trim()}
                  className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm py-2 rounded-lg"
                >
                  {createDraftMut.isPending ? 'Creating…' : 'Save Draft'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
